import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Login from "./Login";
import Dashboard from "./Dashboard";
import Products from "./Products";
import Suppliers from "./Suppliers";
import Customers from "./Customers";
import Vehicles from "./Vehicles";
import Purchase from "./Purchase";
import Sale from "./Sale";
import Receipt from "./Receipt";
import Voucher from "./Voucher";
import Expenses from "./Expenses";
import Accounts from "./Accounts";
import SalesReport from "./SalesReport";
import PurchaseReport from "./PurchaseReport";
import AccountReport from "./AccountReport";
import StockReport from "./StockReport";
import DailySummaryReport from "./DailySummaryReport";
import ProfitLossReport from "./ProfitLossReport";
import PaymentsReport from "./PaymentsReport";
import CustomerLedgerReport from "./CustomerLedgerReport";
import SupplierLedgerReport from "./SupplierLedgerReport";
import ExpensesReport from "./ExpensesReport";
import FirmSettings from "./FirmSettings";
import UserSettings from "./UserSettings";
import CustomerBills from "./CustomerBills";

const REQUEST_STATE_EVENT = "app-request-state";

function GlobalRequestOverlay() {
  const [state, setState] = useState({
    activeCount: 0,
    label: "",
    startedAt: null,
    sessionId: ""
  });
  const [visible, setVisible] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const handleRequestState = (event) => {
      const detail = event?.detail || {};
      setState({
        activeCount: Number(detail.activeCount || 0),
        label: String(detail.label || ""),
        startedAt: detail.startedAt || null,
        sessionId: String(detail.sessionId || "")
      });
    };

    window.addEventListener(REQUEST_STATE_EVENT, handleRequestState);
    return () => {
      window.removeEventListener(REQUEST_STATE_EVENT, handleRequestState);
    };
  }, []);

  useEffect(() => {
    let timer = null;
    if (state.activeCount > 0) {
      timer = window.setTimeout(() => setVisible(true), 350);
    } else {
      setVisible(false);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [state.activeCount]);

  useEffect(() => {
    if (!visible || !state.startedAt) {
      setElapsedSeconds(0);
      return undefined;
    }
    const tick = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - Number(state.startedAt)) / 1000)));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [state.startedAt, visible]);

  if (!visible || state.activeCount <= 0) return null;

  return (
    <div className="global-request-overlay">
      <div className="global-request-card shadow">
        <div className="spinner-border text-primary" role="status" />
        <h5 className="mt-3 mb-2">Please Wait</h5>
        <div className="text-muted">{state.label || "Processing request"}</div>
        <div className="small text-muted mt-2">
          Server reply is taking time. Request is still running, please wait.
        </div>
        <div className="small mt-2">Pending Requests: {state.activeCount}</div>
        <div className="small text-muted">Processing Time: {elapsedSeconds}s+</div>
        {state.sessionId ? (
          <div className="small text-muted">Session: {state.sessionId}</div>
        ) : null}
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/customer/bills" element={<CustomerBills />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/products" element={<Products />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/vehicles" element={<Vehicles />} />
          <Route path="/purchase" element={<Purchase />} />
          <Route path="/sale" element={<Sale />} />
          <Route path="/receipt" element={<Receipt />} />
          <Route path="/voucher" element={<Voucher />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/reports/sale" element={<SalesReport />} />
          <Route path="/reports/purchase" element={<PurchaseReport />} />
          <Route path="/reports/daily-summary" element={<DailySummaryReport />} />
          <Route path="/reports/payments" element={<PaymentsReport />} />
          <Route path="/reports/customer-ledger" element={<CustomerLedgerReport />} />
          <Route path="/reports/supplier-ledger" element={<SupplierLedgerReport />} />
          <Route path="/reports/expenses" element={<ExpensesReport />} />
          <Route path="/reports/profit-loss" element={<ProfitLossReport />} />
          <Route path="/reports/account" element={<AccountReport />} />
          <Route path="/reports/stock" element={<StockReport />} />
          <Route path="/settings/firm" element={<FirmSettings />} />
          <Route path="/settings/user" element={<UserSettings />} />
        </Routes>
        <GlobalRequestOverlay />
      </>
    </BrowserRouter>
  );
}

export default App;
